import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api, { authAPI, notificationsAPI } from '../services/api';
import { useToast } from './Toast';
import {
  LayoutDashboard, Users, CheckSquare, Home, Activity, 
  BarChart3, Bell, Settings, LogOut, Menu, X, History,
  ChevronLeft, ChevronRight, Search, Grid
} from 'lucide-react';

const Layout = ({ children, setIsAuthenticated }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [user, setUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [logoError, setLogoError] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [showLoginSummary, setShowLoginSummary] = useState(false);
  const lastNotificationIdRef = useRef(0);
  const streamConnectedRef = useRef(false);
  const seenNotificationIdsRef = useRef(new Set());
  const reconnectAttemptsRef = useRef(0);

  useEffect(() => {
    const hydrateUser = async () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          setUser(JSON.parse(userData));
          return;
        } catch (e) {
          localStorage.removeItem('user');
        }
      }

      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const meRes = await authAPI.getCurrentUser();
        if (meRes?.data) {
          setUser(meRes.data);
          localStorage.setItem('user', JSON.stringify(meRes.data));
        }
      } catch (e) {
        // authAPI interceptor will broadcast logout on 401
      }
    };

    hydrateUser().finally(() => {
      loadNotificationState();
    });
  }, []);

  useEffect(() => {
    const handleAuthLogout = () => {
      setIsAuthenticated(false);
      navigate('/login');
    };
    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, [navigate, setIsAuthenticated]);

  const loadNotificationState = async () => {
    try {
      let nextCount = 0;
      let list = [];

      try {
        const [countRes, listRes] = await Promise.all([
          notificationsAPI.getUnreadCount(),
          notificationsAPI.getAll({ limit: 50 })
        ]);
        nextCount = Number(countRes.data?.count || 0);
        list = Array.isArray(listRes.data) ? listRes.data : [];
      } catch (primaryErr) {
        // Fallback: if unread-count endpoint isn't reachable in a given deployment,
        // still show badge/banner by counting unread rows.
        const [unreadRes, listRes] = await Promise.all([
          notificationsAPI.getAll({ unread_only: 'true', limit: 200 }),
          notificationsAPI.getAll({ limit: 50 })
        ]);
        const unreadList = Array.isArray(unreadRes.data) ? unreadRes.data : [];
        nextCount = unreadList.filter((n) => n && n.is_read === false).length;
        list = Array.isArray(listRes.data) ? listRes.data : [];
      }

      setUnreadCount(nextCount);
      setNotifications(list);
      setShowLoginSummary(nextCount > 0);
      const maxId = Array.isArray(list) ? Math.max(0, ...list.map((n) => Number(n?.id) || 0)) : 0;
      lastNotificationIdRef.current = Math.max(lastNotificationIdRef.current || 0, maxId);
      if (Array.isArray(list)) {
        list.forEach((n) => {
          if (n?.id != null) seenNotificationIdsRef.current.add(String(n.id));
        });
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const navigateToLeadDetail = (leadId) => {
    const returnTo = `${location.pathname}${location.search}` || '/';
    const returnScroll = typeof window !== 'undefined' ? window.scrollY : 0;
    navigate(`/leads/${leadId}`, { state: { returnTo, returnScroll } });
  };

  const emitToastForNotification = (incoming) => {
    const leadName = incoming?.lead_name || 'Lead';
    const projectName = incoming?.project_name || 'General';
    const messageText = incoming?.message || 'New notification received';
    showToast({
      type: 'info',
      title: incoming?.title || 'New Notification',
      message: `${messageText} - ${leadName}, ${projectName}`,
      duration: 6000,
      actionLabel: incoming?.related_id ? 'View' : null,
      onAction: incoming?.related_id ? () => navigateToLeadDetail(incoming.related_id) : null
    });
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    let source;
    let reconnectTimer;
    // Derive origin from the same baseURL axios uses (prevents SSE pointing at wrong host).
    const apiBase = String(api?.defaults?.baseURL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
    const openStream = () => {
      try {
        const lastId = Number(lastNotificationIdRef.current || 0);
        const url = `${apiBase}/api/notifications/stream?token=${encodeURIComponent(token)}&last_id=${encodeURIComponent(String(lastId))}`;
        source = new EventSource(url);
        source.onopen = () => {
          streamConnectedRef.current = true;
          reconnectAttemptsRef.current = 0;
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('[notifications] stream open');
          }
        };

        source.addEventListener('connected', (event) => {
          streamConnectedRef.current = true;
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('[notifications] stream connected', event?.data);
          }
        });

        source.addEventListener('new_notification', (event) => {
          try {
            const incoming = JSON.parse(event.data);
            const incomingId = String(incoming?.id ?? '');
            if (!incomingId) return;
            if (seenNotificationIdsRef.current.has(incomingId)) return;
            seenNotificationIdsRef.current.add(incomingId);
            lastNotificationIdRef.current = Math.max(lastNotificationIdRef.current || 0, Number(incoming?.id) || 0);
            setNotifications((prev) => [incoming, ...prev].slice(0, 100));
            setUnreadCount((prev) => prev + (incoming.is_read ? 0 : 1));
            emitToastForNotification(incoming);
          } catch (err) {
            console.error('Failed to parse notification stream event:', err);
          }
        });

        source.onerror = () => {
          try {
            source?.close();
          } catch (e) {
            // ignore
          }
          source = null;
          streamConnectedRef.current = false;

          // Stop reconnecting if auth is gone — a 401 will never resolve
          if (!localStorage.getItem('token')) {
            if (process.env.NODE_ENV === 'development') {
              console.log('[notifications] no token; stopping reconnect');
            }
            reconnectAttemptsRef.current = 0;
            return;
          }

          reconnectAttemptsRef.current += 1;
          if (reconnectAttemptsRef.current > 5) {
            if (process.env.NODE_ENV === 'development') {
              console.log('[notifications] max reconnect attempts reached; giving up');
            }
            reconnectAttemptsRef.current = 0;
            return;
          }

          if (process.env.NODE_ENV === 'development') {
            console.log(`[notifications] stream error (attempt ${reconnectAttemptsRef.current}); reconnecting soon`);
          }
          if (!reconnectTimer) {
            const delay = Math.min(2000 * reconnectAttemptsRef.current, 30000);
            reconnectTimer = setTimeout(() => {
              reconnectTimer = null;
              openStream();
            }, delay);
          }
        };
      } catch (error) {
        console.error('Failed to open notification stream:', error);
      }
    };

    openStream();

    const pollId = setInterval(() => {
      loadNotificationState();
    }, 30000);

    // Fast fallback polling when SSE is blocked by hosting/proxy/CORS.
    // This ensures agents still get toasts + badge updates within ~2s.
    const fastPollId = setInterval(async () => {
      if (streamConnectedRef.current) return;
      try {
        const res = await notificationsAPI.getAll({ limit: 10 });
        const list = Array.isArray(res.data) ? res.data : [];
        if (list.length === 0) return;

        // Process from oldest -> newest so toasts appear in order.
        const sorted = [...list].sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0));
        for (const n of sorted) {
          const nid = String(n?.id ?? '');
          if (!nid) continue;
          if (seenNotificationIdsRef.current.has(nid)) continue;
          if ((Number(n?.id) || 0) <= (Number(lastNotificationIdRef.current) || 0)) {
            // Still mark as seen to avoid repeated work.
            seenNotificationIdsRef.current.add(nid);
            continue;
          }
          seenNotificationIdsRef.current.add(nid);
          lastNotificationIdRef.current = Math.max(lastNotificationIdRef.current || 0, Number(n?.id) || 0);
          setNotifications((prev) => [n, ...prev].slice(0, 100));
          setUnreadCount((prev) => prev + (n.is_read ? 0 : 1));
          emitToastForNotification(n);
        }
      } catch (e) {
        // ignore; we'll retry
      }
    }, 2000);

    return () => {
      if (source) source.close();
      clearInterval(pollId);
      clearInterval(fastPollId);
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, showToast]);

  const formatRelativeTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  const handleOpenNotifications = async () => {
    setNotificationPanelOpen(true);
    setShowLoginSummary(false);
    await loadNotificationState();
  };

  const markOneAsRead = async (notification) => {
    try {
      if (!notification.is_read) {
        await notificationsAPI.markAsRead(notification.id);
        setUnreadCount((prev) => Math.max(0, prev - 1));
        setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n)));
      }
      if (notification.related_id) {
        setNotificationPanelOpen(false);
        navigateToLeadDetail(notification.related_id);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    navigate('/login');
  };

  const baseMenuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/leads', icon: Users, label: 'Leads' },
    { path: '/tasks', icon: CheckSquare, label: 'Tasks' },
    { path: '/properties', icon: Home, label: 'Properties' },
    { path: '/analytics', icon: BarChart3, label: 'Analytics' },
  ];

  const adminMenuItems = [
    { path: '/users', icon: Users, label: 'Users' },
    { path: '/admin-activity', icon: Activity, label: 'Admin Activity' },
    { path: '/audit-log', icon: History, label: 'Audit Log' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  const allMenuItems = user?.role === 'admin' ? [...baseMenuItems, ...adminMenuItems] : baseMenuItems;

  return (
    <>
      <div className="flex h-screen bg-[#F5F4F2] overflow-hidden">
        {/* Sidebar - on mobile: overlay when open; on lg+: always fixed and pushes content */}
        <aside className={`${sidebarOpen ? 'w-[220px]' : 'w-20'} fixed inset-y-0 left-0 z-40 bg-white transition-all duration-300 flex flex-col border-r border-gray-200 overflow-hidden ${!sidebarOpen ? '-translate-x-full lg:translate-x-0' : 'translate-x-0'} lg:fixed`}>
          {/* Logo */}
          <div className="h-14 flex items-center justify-between px-4 relative border-b border-gray-100">
            {sidebarOpen ? (
              <div className="flex items-center gap-2.5">
                <Grid size={18} className="text-[#1E3A5F]" />
                <div>
                  <h1 className="text-gray-900 font-semibold text-sm">LuxeGrid CRM</h1>
                  <p className="text-gray-500 text-[10px]">Real Estate Management</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center w-full">
                <Grid size={18} className="text-[#1E3A5F]" />
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="absolute top-1.5 right-1.5 p-1 text-gray-500 rounded-full hover:bg-gray-100 transition-all duration-150"
            >
              {sidebarOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-3 overflow-y-auto">
            <ul className="space-y-0.5">
              {baseMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center ${sidebarOpen ? 'gap-2.5' : 'justify-center'} px-3 py-2 text-sm rounded-md transition-all duration-150 border-l-2 ${
                        isActive
                          ? 'text-gray-900 font-semibold border-l-[#1E3A5F] bg-gray-50'
                          : 'text-gray-500 border-l-transparent hover:text-gray-800 hover:bg-gray-50'
                      }`}
                      title={!sidebarOpen ? item.label : undefined}
                    >
                      <Icon size={16} strokeWidth={1.5} className={sidebarOpen ? '' : 'flex-shrink-0'} />
                      {sidebarOpen && <span>{item.label}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>

            {user?.role === 'admin' && (
              <>
                <hr className="border-gray-200 my-3" />
                <div className="px-3 mb-1.5">
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider">Admin</span>
                </div>
                <ul className="space-y-0.5">
                  {adminMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          className={`flex items-center ${sidebarOpen ? 'gap-2.5' : 'justify-center'} px-3 py-2 text-sm rounded-md transition-all duration-150 border-l-2 ${
                            isActive
                              ? 'text-gray-900 font-semibold border-l-[#1E3A5F] bg-gray-50'
                              : 'text-gray-500 border-l-transparent hover:text-gray-800 hover:bg-gray-50'
                          }`}
                          title={!sidebarOpen ? item.label : undefined}
                        >
                          <Icon size={16} strokeWidth={1.5} className={sidebarOpen ? '' : 'flex-shrink-0'} />
                          {sidebarOpen && <span>{item.label}</span>}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </nav>

          {/* User Section */}
          <div className="p-3 mt-auto">
            {sidebarOpen && (
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-7 h-7 bg-[#1E3A5F] text-white rounded-full flex items-center justify-center text-xs font-medium">
                  {user?.full_name ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                </div>
                <div>
                  <p className="text-gray-800 text-sm font-medium">{user?.full_name || user?.username}</p>
                  <p className="text-gray-400 text-[11px] capitalize">{user?.role}</p>
                </div>
              </div>
            )}
            <button
              onClick={handleLogout}
              className={`w-full flex ${sidebarOpen ? 'gap-2.5' : 'justify-center'} items-center px-3 py-1.5 text-gray-400 hover:text-red-500 transition-all duration-150 text-sm`}
              title={!sidebarOpen ? 'Logout' : undefined}
            >
              <LogOut size={16} strokeWidth={1.5} className={sidebarOpen ? '' : 'flex-shrink-0'} />
              {sidebarOpen && <span>Logout</span>}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <div className={`flex-1 flex flex-col overflow-hidden min-w-0 ${sidebarOpen ? 'lg:ml-[220px]' : 'lg:ml-20'}`}>
          {/* Top Bar */}
          <header className="bg-[#F5F4F2] border-b border-gray-200/60 flex items-center justify-between px-4 md:px-6 relative">
            <div className="h-14 flex items-center gap-2 md:gap-3">
              <button type="button" onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg" aria-label="Open menu">
                <Menu size={24} />
              </button>
            </div>
            
            <div className="flex items-center gap-2 md:gap-3">
              <div className="relative hidden lg:block">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-56 !pl-10 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button
                type="button"
                onClick={handleOpenNotifications}
                className="relative p-2 text-gray-600 hover:text-blue-600 rounded-lg transition-all duration-150 hover:bg-gray-50"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {unreadCount}
                  </span>
                )}
              </button>
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
                {user?.full_name ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
              </div>
            </div>
            
          </header>

          {/* Mobile sidebar backdrop */}
          {sidebarOpen && (
            <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
          )}

          {showLoginSummary && unreadCount > 0 && (
            <div className="px-6 py-2 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
              <p className="text-sm text-blue-800">
                You have {unreadCount} new {unreadCount === 1 ? 'notification' : 'notifications'} while you were away.
              </p>
              <button
                type="button"
                onClick={handleOpenNotifications}
                className="text-sm font-medium text-blue-700 hover:text-blue-900 underline"
              >
                View all
              </button>
            </div>
          )}

          {/* Page Content */}
          <main className="flex-1 overflow-y-auto overflow-x-auto bg-[#F5F4F2] relative pb-16 md:pb-0">
            {children}
          </main>
        </div>
      </div>

      {notificationPanelOpen && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setNotificationPanelOpen(false)} />
          <aside className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl border-l border-gray-200 flex flex-col">
            <div className="px-4 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                <p className="text-xs text-gray-500">{unreadCount} unread</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  className="text-xs font-medium text-blue-600 hover:text-blue-800"
                >
                  Mark all as read
                </button>
                <button
                  type="button"
                  onClick={() => setNotificationPanelOpen(false)}
                  className="p-1 text-gray-500 hover:text-gray-800"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="p-6 text-sm text-gray-500">No notifications yet.</p>
              ) : (
                notifications.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => markOneAsRead(n)}
                    className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 ${n.is_read ? 'bg-white' : 'bg-blue-50/40'}`}
                  >
                    <p className="text-sm font-medium text-gray-900">{n.lead_name || 'Lead notification'}</p>
                    <p className="text-sm text-gray-700 mt-1">{n.message}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs text-gray-500">{n.project_name || 'General'}</span>
                      <span className="text-xs text-gray-500">{formatRelativeTime(n.created_at)}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </aside>
        </>
      )}

    </>
  );
};

export default Layout;